import acerto from '@acerto/web-sdk';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createHead } from '@vueuse/head';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';

import App from '@/App.vue';
import { currency, formatDate } from '@/helpers/Filters';
import i18n from '@/modules/i18n';
import createQueryClient from '@/plugins/vueQuery';
import router from '@/router';
import { salvarContextoEventosServiceWorker } from '@shared/pwa/utils/contextoEventosServiceWorker';
import { escutarDeeplinkDoServiceWorker } from '@shared/pwa/utils/deeplinkDoServiceWorker';
import { removerAssinaturaComPermissaoRevogada } from '@shared/pwa/utils/pushSubscription';
import { registrarServiceWorker } from '@shared/pwa/utils/registroServiceWorker';
import { desregistrarServiceWorkerLegado } from '@shared/pwa/utils/serviceWorkerLegado';
import initDataDog from './datadog';
import logger from './logger';

import '@/assets/styles/index.scss';

(globalThis as typeof globalThis & { logger: typeof logger }).logger = logger;

acerto.initialize({
  environment: appSettings.VUE_APP_AMBIENTE as 'dev' | 'hmg' | 'prd',
});

initDataDog();
void registrarServiceWorker().then(desregistrarServiceWorkerLegado);
void salvarContextoEventosServiceWorker();
void removerAssinaturaComPermissaoRevogada();

const app = createApp(App);
app.use(router);
escutarDeeplinkDoServiceWorker(router);
app.use(i18n);
app.use(createPinia());

app.use(createHead());

const queryClient = createQueryClient();

app.use(VueQueryPlugin, { queryClient });
app.use(VueReCaptcha, {
  siteKey: appSettings.VUE_APP_RECAPTCHA_SITE_KEY,
  loaderOptions: {
    autoHideBadge: true,
    useEnterprise: true,
  },
});

app.config.errorHandler = (err, instance, info) => {
  // Garante que o erro seja sempre uma instância de Error
  let errorToLog: Error;
  if (err instanceof Error) {
    errorToLog = err;
  } else if (typeof err === 'string') {
    errorToLog = new Error(err);
  } else if (err && typeof err === 'object') {
    const errorMessage = (err as { details?: string; error?: string; message?: string }).details
      || (err as { details?: string; error?: string; message?: string }).error
      || (err as { details?: string; error?: string; message?: string }).message
      || JSON.stringify(err);
    errorToLog = new Error(errorMessage);
    Object.assign(errorToLog, err);
  } else {
    errorToLog = new Error(String(err));
  }

  logger.error.andTrackError(errorToLog, { instance, info });
};

app.config.globalProperties.$filters = {
  currency,
  formatDate,
};
app.mount('#app');

logger.printBanner();
